import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Globe, X } from 'lucide-react';
import {
  LOCALES,
  SITE_LOCALE,
  isMarketingPath,
  urlOnLocale,
  type Locale,
} from '@/lib/site-config';
import { trackEvent } from '@/lib/analytics';

/**
 * Suggests the visitor's own language when they land on a domain that isn't in
 * it — e.g. a Dutch browser on logpulse.io gets "Deze site is ook in het
 * Nederlands beschikbaar".
 *
 * Deliberately a suggestion, not a redirect. Auto-redirecting on Accept-Language
 * hides the .io page from a visitor who wants English, breaks shared links, and
 * confuses crawlers (which mostly send no Accept-Language at all) — the hreflang
 * tags in site-config are what search engines should act on.
 */

const STORAGE_KEY = 'logpulse-website-locale-suggestion';

/** Copy lives in the language being offered — that's the one we know the visitor reads. */
const PROMPTS: Record<Locale, { message: string; accept: string; dismiss: string; close: string; region: string }> = {
  en: {
    message: 'This site is also available in English.',
    accept: 'Switch to English',
    dismiss: 'No thanks',
    close: 'Dismiss',
    region: 'Language suggestion',
  },
  nl: {
    message: 'Deze site is ook in het Nederlands beschikbaar.',
    accept: 'Ga naar Nederlands',
    dismiss: 'Nee, bedankt',
    close: 'Sluiten',
    region: 'Taalsuggestie',
  },
  de: {
    message: 'Diese Website ist auch auf Deutsch verfügbar.',
    accept: 'Zu Deutsch wechseln',
    dismiss: 'Nein, danke',
    close: 'Schließen',
    region: 'Sprachvorschlag',
  },
  fr: {
    message: 'Ce site est également disponible en français.',
    accept: 'Passer au français',
    dismiss: 'Non, merci',
    close: 'Fermer',
    region: 'Suggestion de langue',
  },
  es: {
    message: 'Este sitio también está disponible en español.',
    accept: 'Cambiar a español',
    dismiss: 'No, gracias',
    close: 'Cerrar',
    region: 'Sugerencia de idioma',
  },
  it: {
    message: 'Questo sito è disponibile anche in italiano.',
    accept: "Passa all'italiano",
    dismiss: 'No, grazie',
    close: 'Chiudi',
    region: 'Suggerimento lingua',
  },
};

/**
 * First browser language we have a domain for. Region subtags are dropped, so
 * nl-BE lands on the Dutch site and de-AT/de-CH on the German one — the same
 * routing the EXTRA_HREFLANGS in site-config declare to search engines.
 *
 * Returns null as soon as the current locale is the better match: a visitor
 * whose list is ['en-GB', 'nl'] on logpulse.io is already home and gets nothing.
 */
function preferredLocale(languages: readonly string[]): Locale | null {
  for (const tag of languages) {
    const base = tag.toLowerCase().split('-')[0];
    if (base === SITE_LOCALE) return null;
    if ((LOCALES as readonly string[]).includes(base)) return base as Locale;
  }
  return null;
}

/**
 * The prerender step drives a real browser over the built bundle on 127.0.0.1
 * (scripts/prerender.ts), and page.content() would bake whatever it renders
 * into every static page. Same guard as the RUM beacon in lib/analytics.ts:
 * a production bundle on localhost is the prerenderer, never a visitor. The dev
 * server is not a production build, so the banner stays visible locally.
 */
function isPrerender(): boolean {
  if (!import.meta.env.PROD) return false;
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
}

export function LocaleSuggestion() {
  const { pathname } = useLocation();
  const [target, setTarget] = useState<Locale | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || isPrerender()) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Docs, blog and guides don't exist on every domain (site-config's
    // isEnOnlyPath / per-locale guide slugs), so switching would land the
    // visitor on a redirect or a 404. Offer the switch where the same page
    // genuinely exists; elsewhere stay quiet.
    if (!isMarketingPath(pathname)) return;

    const languages = navigator.languages?.length
      ? navigator.languages
      : [navigator.language].filter(Boolean);
    const suggested = preferredLocale(languages);
    if (!suggested) return;

    setTarget(suggested);
    trackEvent('locale_suggestion_shown', { from: SITE_LOCALE, to: suggested });
    // Runs once per page load: a route change shouldn't re-open a banner the
    // visitor already answered, and the answer is persisted anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!target) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') close('dismissed');
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [target]);

  function close(reason: 'dismissed' | 'accepted') {
    try {
      localStorage.setItem(STORAGE_KEY, reason);
    } catch {
      // Private mode / storage disabled: the banner still closes for this page
      // view, it just comes back on the next one. Not worth failing over.
    }
    setTarget(null);
  }

  if (!target) return null;
  const copy = PROMPTS[target];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={copy.region}
      lang={target}
      className="fixed inset-x-4 bottom-4 z-50 mx-auto w-auto max-w-md animate-slide-up rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl sm:inset-x-auto sm:left-4"
    >
      <div className="flex items-start gap-3 p-4">
        <Globe className="mt-0.5 h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-700 dark:text-gray-200">{copy.message}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <a
              href={urlOnLocale(pathname, target)}
              onClick={() => {
                trackEvent('locale_suggestion_accepted', { from: SITE_LOCALE, to: target });
                close('accepted');
              }}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              {copy.accept}
            </a>
            <button
              type="button"
              onClick={() => {
                trackEvent('locale_suggestion_dismissed', { from: SITE_LOCALE, to: target });
                close('dismissed');
              }}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {copy.dismiss}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            trackEvent('locale_suggestion_dismissed', { from: SITE_LOCALE, to: target });
            close('dismissed');
          }}
          aria-label={copy.close}
          className="-m-1 shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
